import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useState } from 'react';

interface Tag {
  tag: string;
  type: 'person' | 'place' | 'thing';
  count: number;
}

export default function TagsPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuthStore();
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<{ tag: string; type: string } | null>(null);
  const [managementMode, setManagementMode] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['all-tags'],
    queryFn: () => api.listAllTags(),
  });

  const deleteTagMutation = useMutation({
    mutationFn: ({ tag, type }: { tag: string; type: string }) => 
      api.deleteTagGlobally(tag, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-tags'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setPendingDelete(null);
    },
    onError: (err) => {
      console.error('Failed to delete tag:', err);
      alert('Failed to delete tag. Please try again.');
    },
  });

  const tags: Tag[] = data?.tags || [];

  // Group tags by type and sort alphabetically
  const personTags = tags.filter(t => t.type === 'person').sort((a, b) => a.tag.localeCompare(b.tag));
  const placeTags = tags.filter(t => t.type === 'place').sort((a, b) => a.tag.localeCompare(b.tag));
  const thingTags = tags.filter(t => t.type === 'thing').sort((a, b) => a.tag.localeCompare(b.tag));

  const handleTagClick = (tag: string, type: 'person' | 'place' | 'thing') => {
    // Navigate to feed with search params
    navigate(`/feed?tag=${encodeURIComponent(tag)}&type=${type}`);
  };

  const handleDelete = (tag: string, type: string) => {
    setPendingDelete({ tag, type });
  };

  const confirmDelete = () => {
    if (pendingDelete) {
      deleteTagMutation.mutate(pendingDelete);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'person': return '👤';
      case 'place': return '📍';
      case 'thing': return '🏷️';
      default: return '🏷️';
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-2 min-w-0 flex-1">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 break-words">Browse by Tags</h1>
          <p className="text-gray-600 text-sm sm:text-base break-words">
            {managementMode 
              ? '⚠️ Management Mode: Click X to delete tags from all photos' 
              : 'Explore photos by people, places, and things. Click any tag to see related photos.'}
          </p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setManagementMode(!managementMode)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              managementMode
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {managementMode ? '✓ Done Managing' : '🗑️ Manage Tags'}
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-500 border-t-transparent"></div>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-600">Failed to load tags. Please try again.</p>
        </div>
      )}

      {/* Tags Grid */}
      {!isLoading && !isError && (
        <div className="space-y-8">
          {/* People */}
          {personTags.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                  <span className="text-2xl">👤</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">People</h2>
                  <p className="text-sm text-gray-500">{personTags.length} {personTags.length === 1 ? 'person' : 'people'}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {personTags.map(({ tag, count }) => (
                  <div key={tag} className="relative">
                    <button
                      onClick={() => handleTagClick(tag, 'person')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 hover:border-blue-300 rounded-lg transition-colors hover:shadow-md"
                    >
                      <span className="text-sm font-medium text-blue-900">{tag}</span>
                      <span className="text-xs font-semibold text-blue-600 bg-blue-200 px-2 py-0.5 rounded-full">
                        {count}
                      </span>
                    </button>
                    {managementMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(tag, 'person');
                        }}
                        className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm flex items-center justify-center shadow-lg font-bold"
                        title="Delete tag from all photos"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Places */}
          {placeTags.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                  <span className="text-2xl">📍</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Places</h2>
                  <p className="text-sm text-gray-500">{placeTags.length} {placeTags.length === 1 ? 'place' : 'places'}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {placeTags.map(({ tag, count }) => (
                  <div key={tag} className="relative">
                    <button
                      onClick={() => handleTagClick(tag, 'place')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-50 hover:bg-green-100 border border-green-200 hover:border-green-300 rounded-lg transition-colors hover:shadow-md"
                    >
                      <span className="text-sm font-medium text-green-900">{tag}</span>
                      <span className="text-xs font-semibold text-green-600 bg-green-200 px-2 py-0.5 rounded-full">
                        {count}
                      </span>
                    </button>
                    {managementMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(tag, 'place');
                        }}
                        className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm flex items-center justify-center shadow-lg font-bold"
                        title="Delete tag from all photos"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Things */}
          {thingTags.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Things</h2>
                  <p className="text-sm text-gray-500">{thingTags.length} {thingTags.length === 1 ? 'tag' : 'tags'}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {thingTags.map(({ tag, count }) => (
                  <div key={tag} className="relative">
                    <button
                      onClick={() => handleTagClick(tag, 'thing')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 hover:border-purple-300 rounded-lg transition-colors hover:shadow-md"
                    >
                      <span className="text-sm font-medium text-purple-900">{tag}</span>
                      <span className="text-xs font-semibold text-purple-600 bg-purple-200 px-2 py-0.5 rounded-full">
                        {count}
                      </span>
                    </button>
                    {managementMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(tag, 'thing');
                        }}
                        className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm flex items-center justify-center shadow-lg font-bold"
                        title="Delete tag from all photos"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {tags.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
              <span className="text-6xl mb-4 block">🏷️</span>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No tags yet</h3>
              <p className="text-gray-600">
                Start tagging photos to organize your family memories!
              </p>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {pendingDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-2">
              Are you sure you want to delete the tag:
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <span className="text-lg font-semibold text-gray-900">
                {getTypeIcon(pendingDelete.type)} {pendingDelete.tag}
              </span>
              <span className="text-sm text-gray-600 ml-2">
                ({pendingDelete.type})
              </span>
            </div>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              ⚠️ This will delete the tag from ALL photos. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deleteTagMutation.isPending}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteTagMutation.isPending}
                className="btn-danger flex-1 justify-center disabled:opacity-50"
              >
                {deleteTagMutation.isPending ? 'Deleting...' : 'Delete Tag'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

